<template>
  <div class="static-page">
    <div class="static-page-header">
      <dn-headline
        v-if="article.title"
        :text="article.title"
        tag="h1"
      />
      <p
        v-if="article.intro"
        class="lead"
      >
        {{ article.intro }}
      </p>
    </div>

    <div class="dn-content">
      <dn-article-inline-content
        :content="body"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useArticleStore } from '~/store/article';
import { type PublicationContext } from '~/types';
import { type InlineItem } from '~/types/articleInline';

definePageMeta({
  alias: ['/contactus', '/hjelp', '/aboutus', '/terms', '/kontaktoss', '/help', '/omoss', '/vilkar'],
  layout: 'default'
});

const route = useRoute();
const nuxtApp = useNuxtApp();
const articleStore = useArticleStore();
const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

const slug = route.path.replace(/^\/+|\/+$/g, '').toLowerCase();

const staticPagesConfig = (useRuntimeConfig()?.public as unknown as PublicationContext)?.staticPages;
const pageConfig = staticPagesConfig?.[slug];

if (!pageConfig?.contentId) {
  throw createError({
    statusCode: 404,
    statusMessage: `Static page not found: ${slug}`
  });
}

const article = (await articleStore.getArticleContent(pageConfig.contentId)) || {};

if (!article || !article.id) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Static page content not found'
  });
}

nuxtApp.callHook('cpp:data:loaded', { type: 'staticpage', slug, title: useArticle().getMetaTitle() });

const body = computed((): InlineItem[] => {
  return Array.isArray(article.body) ? article.body : [];
});

const { getMetaTitle, getMetaDescription } = useArticle();

const requestURL = useRequestURL();
const pageUrl = `${requestURL.protocol}//${rtConfPub.hostname}${route.path}`;
const ogSite = rtConfPub?.seo?.siteName || useRequestURL()?.hostname.replace('www.', '');

function getMeta () {
  const headMeta = [
    {
      name: 'description',
      content: getMetaDescription
    },
    {
      property: 'og:title',
      content: getMetaTitle
    },
    {
      property: 'og:description',
      content: getMetaDescription
    },
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:url',
      content: pageUrl
    },
    {
      property: 'og:site_name',
      content: ogSite
    },
    {
      name: 'twitter:site',
      content: ogSite
    },
    {
      name: 'twitter:title',
      content: getMetaTitle
    },
    {
      name: 'twitter:description',
      content: getMetaDescription
    },
    {
      name: 'twitter:creator',
      content: ogSite
    },
    {
      name: 'twitter:domain',
      content: rtConfPub.hostname
    }
  ];

  const robotsDefault = (rtConfPub?.seo?.robots || 'index,follow').split(',');
  const robots = [];
  robots.push(robotsDefault.includes('noindex') ? 'noindex' : 'index');
  robots.push(robotsDefault.includes('nofollow') ? 'nofollow' : 'follow');

  headMeta.push({ name: 'robots', content: robots.join(',') });

  return headMeta;
}

useHead({
  title: getMetaTitle,
  meta: getMeta()
});
</script>

<style lang="scss">
.static-page {
  .static-page-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin-bottom: var(--row-gap-article);

    .dn-headline {
      inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
      margin-left: auto;
      margin-right: auto;
      margin-top: var(--row-gap);
      font-size: var(--typography-editorial-titlepiece-min-font-size);
      font-family: var(--typography-editorial-titlepiece-min-font-family);
      font-weight: var(--typography-editorial-titlepiece-min-font-weight);
      line-height: var(--typography-editorial-titlepiece-min-line-height);
      letter-spacing: var(--typography-editorial-titlepiece-min-letter-spacing);

      @media only screen and (width >= 768px) {
        font-size: var(--typography-editorial-titlepiece-max-font-size);
        line-height: var(--typography-editorial-titlepiece-max-line-height);
        letter-spacing: var(--typography-editorial-titlepiece-max-letter-spacing);
      }
    }

    .lead {
      inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
      margin-left: auto;
      margin-right: auto;
      margin-top: var(--row-gap);
      font-size: var(--typography-editorial-lead-min-font-size);
      font-family: var(--typography-editorial-lead-min-font-family);
      font-weight: var(--typography-editorial-lead-min-font-weight);
      line-height: var(--typography-editorial-lead-min-line-height);
      color: var(--color-typography-secondary);

      @media only screen and (width >= 768px) {
        font-size: var(--typography-editorial-lead-max-font-size);
        line-height: var(--typography-editorial-lead-max-line-height);
      }
    }
  }
}
</style>
